import React, { useEffect } from 'react';
import { Button, Drawer, Label, TextInput, Checkbox } from 'flowbite-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { postCodeYup } from '../../../resolvers';
import { usePostCodeContext } from '../../../context/postCodeContext';
import { PostCodeType } from '../../../types/post_code';
import { useReducerAsState } from '../../../hooks/useReducerAsState';
interface PostCodeFormProps {
    postCodeId?: number;
    onClose?: () => void;
    isOpenSideDrawer: boolean;
    setIsOpenSideDrawer: (value: boolean) => void;
}

interface IFormInput {
    title: string;
    post_code: number;
    latitude: string;
    longitude: string;
    id?: number;
    active?: boolean;
    ends_at?: string;
}

interface State {
    loading: boolean;
}

const PostCodeForm = (props: PostCodeFormProps) => {
    const [state, setState] = useReducerAsState<State>({
        loading: false
    });

    const { loading } = state;

    const { createPostCode, updatePostCode, getPostCode } = usePostCodeContext();
    const { isOpenSideDrawer, setIsOpenSideDrawer, onClose } = props;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(postCodeYup)
    });

    const _showError = (error: string) => {
        if (error.length === 0) return;
        toast.error(error);
    };

    const _setLoading = (loading: boolean) => {
        setState({ loading });
    };

    const _handleCloseSideDrawer = () => {
        setIsOpenSideDrawer(false);
        if (onClose) onClose();
    };

    const _clearForm = () => {
        setValue('title', '');
        setValue('post_code', 0);
        setValue('latitude', '');
        setValue('longitude', '');
        setValue('active', false);
        setValue('ends_at', '');
        if (props.postCodeId) setValue('id', -1);
    };

    const _setFields = (postCode: PostCodeType) => {
        let endsAt = postCode.ends_at ?? '';
        if (endsAt.length > 0) {
            const newDate = new Date(postCode.ends_at!);
            const date = newDate.getDate() > 9 ? newDate.getDate() : `0${newDate.getDate()}`;
            const month =
                newDate.getMonth() + 1 > 9 ? newDate.getMonth() + 1 : `0${newDate.getMonth() + 1}`;
            endsAt = `${newDate.getFullYear()}-${month}-${date}`;
        }

        setValue('title', postCode.title);
        setValue('post_code', postCode.postal_code);
        setValue('latitude', postCode.latitude);
        setValue('longitude', postCode.longitude);
        setValue('active', !!postCode.active);
        setValue('ends_at', endsAt);
        if (props.postCodeId) setValue('id', props.postCodeId);
    };

    const _onSubmit: SubmitHandler<IFormInput> = async (data) => {
        if (!data.title && !data.post_code && !data.latitude && !data.longitude) {
            toast.error('Debe completar todos los campos.');
            return;
        }

        _setLoading(true);

        const newData: PostCodeType = {
            title: data.title,
            postal_code: data.post_code,
            latitude: data.latitude,
            longitude: data.longitude,
            active: data.active != undefined ? (data.active == true ? 1 : 0) : 0,
            ends_at: data.ends_at
        };

        if (props.postCodeId) {
            newData.id = props.postCodeId;
            // DC: update post code
            const isUpdatedMessage = await updatePostCode(newData);
            _setLoading(false);
            if (isUpdatedMessage.length > 0) {
                _showError(isUpdatedMessage);
                return;
            }
            toast.success('Código postal actualizado correctamente');
            return;
        }

        const isCreatedMessage = await createPostCode({
            title: data.title,
            postal_code: data.post_code,
            latitude: data.latitude,
            longitude: data.longitude,
            active: data.active != undefined ? (data.active == true ? 1 : 0) : 0,
            ends_at: data.ends_at ?? ''
        });
        _setLoading(false);

        if (isCreatedMessage.length > 0) {
            toast.error(isCreatedMessage);
            return;
        }

        toast.success('Código postal creado correctamente');
        _clearForm();
    };

    useEffect(() => {
        const _onEnterScreen = async (postCodeId: number) => {
            _setLoading(true);
            const postCode = await getPostCode(postCodeId);
            _setLoading(false);
            if (postCode === null || typeof postCode === 'string') {
                toast.error(postCode);
                return;
            }
            _setFields(postCode);
        };

        if (props.postCodeId) {
            _onEnterScreen(props.postCodeId);
        }
    }, [props.postCodeId]);

    return (
        <Drawer
            open={isOpenSideDrawer}
            onClose={_handleCloseSideDrawer}
            position="right"
            className="w-full max-w-md">
            <Drawer.Header title="Detalles" titleIcon={() => <></>} />
            <div className="mb-2">
                <div className="mb-2 block">
                    <Label htmlFor="title" value="Título" />
                </div>
                <TextInput
                    id="title"
                    type="text"
                    required
                    disabled={loading}
                    {...register('title')}
                    helperText={errors && errors.title ? errors.title.message : ''}
                    color={errors && errors.title ? 'failure' : 'gray'}
                />
            </div>
            <div className="mb-2">
                <div className="mb-2 block">
                    <Label htmlFor="cp" value="Código postal" />
                </div>
                <TextInput
                    id="cp"
                    type="number"
                    required
                    disabled={loading}
                    {...register('post_code')}
                    helperText={errors && errors.post_code ? errors.post_code.message : ''}
                    color={errors && errors.post_code ? 'failure' : 'gray'}
                />
            </div>
            <div className="mb-2">
                <div className="mb-2 block">
                    <Label htmlFor="ends_at" value="Vigencia" />
                </div>
                <TextInput
                    id="ends_at"
                    type="date"
                    required
                    disabled={loading}
                    {...register('ends_at')}
                    helperText={errors && errors.ends_at ? errors.ends_at.message : ''}
                    color={errors && errors.ends_at ? 'failure' : 'gray'}
                />
            </div>
            <div className="mb-2">
                <div className="flex flex-row justify-between">
                    <div>
                        <div className="mb-2 block">
                            <Label htmlFor="latitude" value="Latitud" />
                        </div>
                        <TextInput
                            id="latitude"
                            type="number"
                            required
                            disabled={loading}
                            {...register('latitude')}
                            helperText={errors && errors.latitude ? errors.latitude.message : ''}
                            color={errors && errors.latitude ? 'failure' : 'gray'}
                        />
                    </div>
                    <div>
                        <div className="mb-2 block">
                            <Label htmlFor="longitude" value="Longitud" />
                        </div>
                        <TextInput
                            id="longitude"
                            type="number"
                            required
                            disabled={loading}
                            {...register('longitude')}
                            helperText={errors && errors.longitude ? errors.longitude.message : ''}
                            color={errors && errors.longitude ? 'failure' : 'gray'}
                        />
                    </div>
                </div>
            </div>
            <div className="mb-2">
                <div className="flex items-center">
                    <Checkbox
                        id="active"
                        {...register('active')}
                        disabled={loading}
                        color={errors && errors.active ? 'failure' : 'gray'}
                    />
                    <div className="ml-2">
                        <Label htmlFor="active" value="¿Activar código postal?" />
                    </div>
                </div>
                <p className="text-xs text-gray-500">
                    Este código postal será visible para todos aquellos que tengan acceso al enlace
                    del mismo. (si está activo)
                </p>
            </div>
            <Button
                isProcessing={loading}
                disabled={loading}
                type="submit"
                className="mt-4"
                color="dark"
                onClick={handleSubmit(_onSubmit)}>
                {props.postCodeId ? 'Editar' : 'Crear'} código postal
            </Button>
        </Drawer>
    );
};

export default PostCodeForm;
